import { IQPlayerData, IQPlayerDataItem } from '../interfaces';
import { cloneDeep } from 'lodash';
import { getAudioFilterIndex } from './player-provider';

export const CROSS_FADE_DURATION = 0; // seconds

export const getDecodedAudioBuffer = async (audioContext: AudioContext, url: string) : Promise<AudioBuffer|null> => {
    try{
        if(!audioContext) return null;

        const response = await fetch(url);
        const arrayBuffer = await response.arrayBuffer();
        return audioContext.decodeAudioData(arrayBuffer)
    }
    catch(ex) {
        console.error(ex);
    }

    return null;
};

export const createGainNode = (audioContext: AudioContext, initialVolume: number) : GainNode => {
    const gainNode = audioContext.createGain();

    gainNode.gain.value = initialVolume;
    gainNode.connect(audioContext.destination);

    return gainNode;
};

export const createAudioSource = (
    audioContext: AudioContext,
    audioBuffer: AudioBuffer,
    gainNode: GainNode
) : AudioBufferSourceNode|null => {
    if(!audioContext || !audioBuffer) return null;

    const source = audioContext.createBufferSource();

    source.buffer = audioBuffer;
    source.connect(gainNode);
    source.loop = true;

    return source;
};

export const loadMP3List = async (data: IQPlayerData|null, audioContext: AudioContext) => {
    if(!data?.presets) return null;

    const copy = cloneDeep(data);

    for(const playlistItem of copy.presets) {
        if(!playlistItem.audios) continue;

        playlistItem.audioSources = [];
        playlistItem.gainNodes = [];
        playlistItem.audioBuffers = [];

        for(const audioUrl of playlistItem.audios) {
            const audioBuffer = await getDecodedAudioBuffer(audioContext, audioUrl);
            if(!audioBuffer) continue;

            const gainNode = createGainNode(audioContext, 0);

            const audioSource = createAudioSource(
                audioContext,
                audioBuffer,
                gainNode
            );
            if(!audioSource) continue;

            playlistItem.audioSources.push(audioSource);
            playlistItem.gainNodes.push(gainNode);
            playlistItem.audioBuffers.push(audioBuffer);
        }
    }

    return copy;
};

export const setFilterVolume = (
    audioContext: AudioContext,
    selected: IQPlayerDataItem|null,
    isBypass: boolean,
    isDelta: boolean,
    crossFadeDuration: number
) => {
    if(!selected?.gainNodes?.length) return;

    try{
        const index = getAudioFilterIndex(isBypass, isDelta);
        const currentTime = audioContext.currentTime;

        for(let i = 0; i<selected.gainNodes.length; i++) {
            const gainNode = selected.gainNodes[i];

            if(i === index) {
                // gainNode.gain.value = 1;

                // Fade in this gain node ------------------
                gainNode.gain.cancelScheduledValues(currentTime);
                gainNode.gain.setValueAtTime(gainNode.gain.value, currentTime);
                gainNode.gain.linearRampToValueAtTime(1, currentTime + crossFadeDuration);
            }
            else{
                // gainNode.gain.value = 0;

                // Fade out this gain node ---------------
                gainNode.gain.cancelScheduledValues(currentTime);
                gainNode.gain.setValueAtTime(gainNode.gain.value, currentTime);
                gainNode.gain.linearRampToValueAtTime(0, currentTime + crossFadeDuration);
            }
        }
    }
    catch(ex) {
        console.error(ex);
    }
};

const playSource = (
    i: number,
    audioContext: AudioContext,
    selected: IQPlayerDataItem
) => {
    // 'AudioBufferSourceNode' cannot call start more than once.
    // We need to recreate them each time.
    const audioSource = createAudioSource(
        audioContext,
        selected.audioBuffers[i],
        selected.gainNodes[i]
    );
    if(!audioSource) return;

    if(audioSource) {
        selected.audioSources[i] = audioSource;

        // The '0' argument specifies the time at which the playback should start,
        // relative to the AudioContext's currentTime.
        selected.audioSources[i]?.start();
    }
};

export const play = (
    audioContext: AudioContext,
    selected: IQPlayerDataItem|null,
    isBypass: boolean,
    isDelta: boolean
): IQPlayerDataItem|null => {
    if(!selected?.gainNodes?.length ||
        !selected?.audioSources?.length ||
        !selected?.audioBuffers?.length) return null;

    try{
        const index = getAudioFilterIndex(isBypass, isDelta);
        const _selected = cloneDeep(selected) as IQPlayerDataItem;

        selected.gainNodes[index].gain.value = 1;
        playSource(index, audioContext, _selected);

        for(let i= 0; i<selected.audioBuffers.length; i++) {
            if(i === index) continue;
            selected.gainNodes[i].gain.value = 0;
            playSource(i, audioContext, _selected);
        }

        return _selected;
    }
    catch(ex) {
        console.error(ex);
    }

    return null;
};

export const stop = (
    selected: IQPlayerDataItem|null,
    isBypass: boolean,
    isDelta: boolean
) => {
    try{
        if(!selected?.audioSources?.length) return;

        const index = getAudioFilterIndex(isBypass, isDelta);
        selected.audioSources[index].stop();

        for(let i=0; i<selected.audioSources.length; i++){
            if(i === index) continue;
            selected.audioSources[i].stop();
        }
    }
    catch(ex) {
        console.error(ex);
    }
};